/** Unlock rules (SPEC 6.2). Tiny on purpose: the map imports it without the whole engine. */

/**
 * Island boss (SPEC 6.2): open once every playable lesson of the island has at least 1 star
 * (or the parent unlocked everything).
 */
export function bossUnlocked(
  lessonIds: readonly string[],
  bestStars: Readonly<Record<string, number>>,
  freeUnlock = false,
): boolean {
  return lessonIds.length > 0 && (freeUnlock || lessonIds.every((id) => (bestStars[id] ?? 0) >= 1));
}

/** The parts of the curriculum index the rules need: volumes → islands → lessons, in order. */
export interface UnlockBook {
  topics: readonly { id: string; lessons: readonly { id: string; playable?: boolean }[] }[];
}

export interface Unlocks {
  lessons: ReadonlySet<string>;
  islands: ReadonlySet<string>;
}

/**
 * Lessons and islands the child may open (SPEC 6.2, D35). Playable lessons open one after another:
 * the first lesson of the first island is open; a lesson opens when the previous one has ≥ 1 star;
 * the first lesson of an island opens when every lesson of the previous island has ≥ 1 star (the
 * chain runs on into volume 2). A lesson the child already played (it appears in `progress`)
 * never locks again. With "Mở khoá tự do" every playable lesson is open.
 */
export function unlocks(
  books: readonly UnlockBook[],
  progress: Readonly<Record<string, { bestStars: number } | undefined>>,
  freeUnlock: boolean,
): Unlocks {
  const lessons = new Set<string>();
  const islands = new Set<string>();
  const star = (id: string) => (progress[id]?.bestStars ?? 0) >= 1;
  let prevIsland: string[] | null = null;
  for (const book of books) {
    for (const topic of book.topics) {
      const ids = topic.lessons.filter((l) => l.playable).map((l) => l.id);
      if (ids.length === 0) continue;
      ids.forEach((id, i) => {
        const open =
          freeUnlock ||
          progress[id] !== undefined ||
          (i === 0
            ? prevIsland === null
              ? true
              : prevIsland.every(star)
            : star(ids[i - 1] as string));
        if (open) lessons.add(id);
      });
      if (ids.some((id) => lessons.has(id))) islands.add(topic.id);
      prevIsland = ids;
    }
  }
  return { lessons, islands };
}

/** Old rule (T5.11, before D49): lessons with ≥ 2★ at a level that opened the next level everywhere */
export const NOTEBOOK_UNLOCK = 5;
export type NotebookLevel = 1 | 2 | 3;
export interface NotebookLevels {
  /** the levels the child may pick */
  open: NotebookLevel[];
  /** set by the parent: the child plays this level only */
  fixed: NotebookLevel | null;
}

/**
 * Notebook input levels of one lesson (SPEC 5.6, D49): 1 "Chọn" always; finishing a level of
 * this lesson with ≥ 2★ opens the next one for this lesson (`passed` = the hardest level finished
 * there with ≥ 2★, 0 = none). Levels opened by the old rule stay open everywhere: `done[l]` =
 * distinct lessons finished with ≥ 2★ at level l or harder. A parent may fix one level.
 */
export function notebookLevels(
  done: { 1: number; 2: number },
  fixed: number | null | undefined,
  passed = 0,
): NotebookLevels {
  if (fixed === 1 || fixed === 2 || fixed === 3) return { open: [fixed], fixed };
  let everywhere = 1;
  if (done[1] >= NOTEBOOK_UNLOCK) everywhere = done[2] >= NOTEBOOK_UNLOCK ? 3 : 2;
  const top = Math.min(3, Math.max(everywhere, passed + 1));
  const open = ([1, 2, 3] as const).filter((l) => l <= top);
  return { open, fixed: null };
}
